// framework/src/common/schemas/collections/apiRegistry.js
import { z } from 'zod';

export const ApiEndpointParamSchema = z.object({
    name:        z.string().min(1),
    description: z.string().optional().default(''),
    type:        z.enum(['string', 'number', 'boolean', 'object', 'array']).default('string'),
    required:    z.boolean().default(false),
    location:    z.enum(['query', 'body', 'path']).default('query'),
});

export const ApiEndpointSchema = z.object({
    name:        z.string().min(1).describe('Machine-readable endpoint name, e.g. get_forecast'),
    description: z.string().optional().default('').describe('What this endpoint does — shown to agent during describe_api'),
    method:      z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']).default('GET'),
    path:        z.string().default('/').describe('Path appended to baseUrl, e.g. /forecast'),
    params:      z.array(ApiEndpointParamSchema).default([]),
});

const ApiRegistrySchema = z.object({
    _id:           z.string().optional(),

    subscriptionId: z.string().optional().nullable()
                    .describe('Tenant-scoped. Null for _source:file or _source:global entries.'),

    // Source determines read/write permissions
    // file   = loaded from server/ai/apiregistry/*.json — read-only, all tenants
    // global = cross-tenant shared DB record — read-only unless publishGlobal privilege
    // db     = tenant-specific, created by user or privileged agent
    _source:        z.enum(['file', 'global', 'db']).default('db'),

    name:           z.string().min(1).describe("Display name, e.g. 'OpenWeather'"),
    description:    z.string().min(1)
                    .describe('Short, keyword-rich description used for agent search/discovery.'),
    documentation:  z.string().optional().default('')
                    .describe('Full markdown reference — endpoint details, examples, error codes. Read by agent before calling.'),

    type:           z.enum(['rest', 'mcp']).default('rest'),

    baseUrl:        z.string().url().describe('Base URL for REST, server URL for MCP'),

    authType:       z.enum(['header', 'query', 'bearer', 'none']).default('none'),
    authKeyName:    z.string().optional().default('')
                    .describe("Header/param name, e.g. 'Authorization', 'api_key', 'X-API-Key'"),
    secretKey:      z.string().optional().default('')
                    .describe("Key name in SecretsManager, e.g. 'openweather_api_key'"),
    secretScope:    z.enum(['subscription', 'agent']).default('subscription'),

    endpoints:      z.array(ApiEndpointSchema).default([]),

    tags:           z.array(z.string()).default([]),
    enabled:        z.boolean().default(true),

    // Set true by platform admins to make available across all subscriptions (_source:'global')
    publishGlobal:  z.boolean().optional().default(false),

    createdAt:      z.coerce.date().optional(),
    updatedAt:      z.coerce.date().optional(),
});

export default ApiRegistrySchema;